import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { useTransition, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";
import type { PlaceLibraryTab, PlaceLibrarySortState } from "@/core/services/types/place.types";
import { buildPlaceLibraryHref } from "@/core/services/types/place.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";

const searchInputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function SearchSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

interface PlacesLibrarySearchFormProps {
  tab: PlaceLibraryTab;
  searchQuery: string;
  sort: PlaceLibrarySortState;
  pageSize: LibraryPageSize;
}

function buildLibraryHref(
  tab: PlaceLibraryTab,
  sort: PlaceLibrarySortState,
  pageSize: LibraryPageSize,
): string {
  return buildPlaceLibraryHref({ tab, page: 1, sort, pageSize });
}

export function PlacesLibrarySearchForm({
  tab,
  searchQuery,
  sort,
  pageSize,
}: PlacesLibrarySearchFormProps) {
  const t = useTranslations("places.library");
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const query = String(formData.get("q") ?? "").trim();
    startTransition(() => {
      navigate(
        buildPlaceLibraryHref({
          tab,
          page: 1,
          searchQuery: query,
          sort,
          pageSize,
        }),
      );
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 border-b border-black/10 px-4 py-3 sm:flex-row sm:items-center"
    >
      <label className="sr-only" htmlFor="places-library-search">
        {t("searchLabel")}
      </label>
      <input
        id="places-library-search"
        type="search"
        name="q"
        defaultValue={searchQuery}
        placeholder={t("searchPlaceholder")}
        disabled={isPending}
        className={searchInputClassName}
      />
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="submit"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? <SearchSpinner /> : null}
          {t("searchSubmit")}
        </Button>
        {searchQuery ? (
          <Link
            to={buildLibraryHref(tab, sort, pageSize)}
            className="inline-flex items-center rounded-xl wireframe-border bg-white px-4 py-2 text-sm text-zinc-900 shadow-sm transition-colors hover:bg-zinc-50"
          >
            {t("clearSearch")}
          </Link>
        ) : null}
      </div>
    </form>
  );
}
